import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { User } from "lucide-react";
import { DAY_HOUR_HEIGHT, layoutDayEvents, WT_COLOR } from "@/lib/schedule";

// columns: [{ key, header (ReactNode), events: [rawEvent] }]
// range: { startH, endH }
const SNAP = 15;
const fmtMin = (m) => format(new Date(2000, 0, 1, Math.floor(m / 60), m % 60), "h:mma");

export function TimeGrid({ columns, range, onEventClick, onSlotSelect, showHeader = true, initialScrollHour = 0 }) {
  const hours = Array.from({ length: range.endH - range.startH }, (_, i) => range.startH + i);
  const bodyHeight = hours.length * DAY_HOUR_HEIGHT;
  const scrollRef = useRef(null);
  const [sel, setSel] = useState(null);
  const selRef = useRef(null);
  const [hover, setHover] = useState(null);

  const minuteAt = (el, clientY) => {
    const y = clientY - el.getBoundingClientRect().top;
    const m = range.startH * 60 + Math.floor((y / DAY_HOUR_HEIGHT) * 60 / SNAP) * SNAP;
    return Math.max(range.startH * 60, Math.min(range.endH * 60 - SNAP, m));
  };
  const span = (s) => {
    const start = Math.min(s.a, s.b);
    let end = Math.max(s.a, s.b) + SNAP;
    if (end - start < 30) end = start + 30;
    return { start, end: Math.min(end, range.endH * 60) };
  };

  const onPointerDown = (c, e) => {
    if (!onSlotSelect || e.button > 0 || e.target.closest("button")) return;
    const el = e.currentTarget;
    const m = minuteAt(el, e.clientY);
    const s = { key: c.key, date: c.date, a: m, b: m, el };
    selRef.current = s;
    setSel(s);
    if (e.pointerType !== "mouse") return;
    e.preventDefault();
    const move = (ev) => {
      const next = { ...selRef.current, b: minuteAt(el, ev.clientY) };
      selRef.current = next;
      setSel(next);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      finish();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const finish = () => {
    const s = selRef.current;
    if (!s) return;
    const { start, end } = span(s);
    selRef.current = null;
    setSel(null);
    onSlotSelect({ date: s.date, startMin: start, endMin: end });
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(0, (initialScrollHour - range.startH) * DAY_HOUR_HEIGHT);
    }
  }, [initialScrollHour, range.startH]);

  return (
    <div className="border border-border/60 rounded-sm bg-background" data-testid="time-grid">
      <div ref={scrollRef} className="max-h-[68vh] overflow-auto">
        {showHeader && (
          <div className="flex border-b border-border/60 sticky top-0 bg-background z-20">
            <div className="w-16 shrink-0 border-r border-border/60" />
            {columns.map((c) => (
              <div key={c.key} className="flex-1 min-w-[130px] border-l border-border/40 first:border-l-0 py-3 text-center">
                {c.header}
              </div>
            ))}
          </div>
        )}
        <div className="flex">
          <div className="w-16 shrink-0 border-r border-border/60 relative" style={{ height: bodyHeight }}>
            {hours.map((h, i) => (
              <div
                key={h}
                className="absolute right-2 text-xs text-muted-foreground"
                style={{ top: i * DAY_HOUR_HEIGHT + 4 }}
              >
                {format(new Date(2000, 0, 1, h, 0), "h a")}
              </div>
            ))}
          </div>
          {columns.map((c) => {
            const evs = layoutDayEvents(c.events);
            return (
              <div
                key={c.key}
                className={`relative flex-1 min-w-[130px] border-l border-border/40 first:border-l-0 ${onSlotSelect ? "cursor-cell select-none" : ""}`}
                style={{ height: bodyHeight }}
                onPointerDown={(e) => onPointerDown(c, e)}
                onPointerUp={(e) => { if (e.pointerType !== "mouse" && selRef.current) finish(); }}
                onPointerMove={(e) => onSlotSelect && !sel && !e.target.closest("button") && setHover({ key: c.key, m: minuteAt(e.currentTarget, e.clientY) })}
                onPointerLeave={() => setHover(null)}
                data-testid={`grid-col-${c.date ? format(c.date, "yyyy-MM-dd") : c.key}`}
              >
                {hover?.key === c.key && !sel && (
                  <div className="absolute left-1 right-1 rounded-sm bg-brand/5 border border-dashed border-brand/30 pointer-events-none text-[10px] text-brand px-1.5"
                    style={{ top: ((hover.m - range.startH * 60) / 60) * DAY_HOUR_HEIGHT, height: (30 / 60) * DAY_HOUR_HEIGHT }}>
                    + {fmtMin(hover.m)}
                  </div>
                )}
                {sel?.key === c.key && (() => {
                  const { start, end } = span(sel);
                  return (
                    <div data-testid="slot-selection" className="absolute left-1 right-1 z-10 rounded-sm bg-gradient-to-b from-brand/25 to-[hsl(272_90%_55%)]/25 border border-brand/60 pointer-events-none px-2 py-1"
                      style={{ top: ((start - range.startH * 60) / 60) * DAY_HOUR_HEIGHT, height: ((end - start) / 60) * DAY_HOUR_HEIGHT }}>
                      <div className="text-[11px] font-medium text-brand">{fmtMin(start)} – {fmtMin(end)}</div>
                      <div className="text-[10px] text-muted-foreground">New booking</div>
                    </div>
                  );
                })()}
                {hours.map((h, i) => (
                  <div key={h} className="absolute left-0 right-0 border-t border-border/40" style={{ top: i * DAY_HOUR_HEIGHT }} />
                ))}
                {evs.length === 0 && !showHeader && (
                  <div className="absolute inset-x-0 top-1/3 flex items-center justify-center text-muted-foreground text-sm pointer-events-none">
                    {onSlotSelect ? "No bookings yet — click or drag on a time to add one." : "No bookings scheduled for this day."}
                  </div>
                )}
                {evs.map((e) => {
                  const top = ((e.startMin - range.startH * 60) / 60) * DAY_HOUR_HEIGHT;
                  const height = Math.max(30, ((e.endMin - e.startMin) / 60) * DAY_HOUR_HEIGHT - 2);
                  const widthPct = 100 / (e.cols || 1);
                  const leftPct = (e.col || 0) * widthPct;
                  const color = e.job.team?.[0]?.color || "#f21b7a";
                  const wt = e.job.kind === "walkthrough";
                  const off = e.job.status === "paused" || e.job.status === "cancelled";
                  const finish = e.dur > 0 ? format(new Date(e.start.getTime() + e.dur * 3600000), "h:mma") : null;
                  const techNames = (e.job.team || []).map((t) => t.name).filter(Boolean).join(", ");
                  return (
                    <button
                      key={e.job.id}
                      onClick={() => onEventClick && onEventClick(e.job)}
                      data-testid={`grid-job-${e.job.id}`}
                      style={{
                        top, height,
                        left: `calc(${leftPct}% + 3px)`,
                        width: `calc(${widthPct}% - 6px)`,
                        borderLeftColor: off ? "#b8b0bb" : wt ? WT_COLOR : color,
                        backgroundColor: off ? "hsl(300 8% 94%)" : wt ? `${WT_COLOR}1f` : `${color}26`,
                        textDecoration: e.job.status === "cancelled" ? "line-through" : undefined,
                        borderStyle: wt ? "dashed" : undefined,
                        opacity: off ? 0.65 : 1,
                      }}
                      className="absolute rounded-sm border-l-4 border border-border/50 p-1.5 text-left overflow-hidden hover:shadow-sm transition-shadow"
                    >
                      <div className="text-[11px] text-muted-foreground leading-tight">
                        {format(e.start, "h:mma")}{finish ? ` – ${finish}` : ""}
                      </div>
                      <div className="text-sm font-medium truncate leading-tight">{e.job.customer?.name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground truncate leading-tight">
                        {wt ? (
                          <span className="inline-block rounded-full px-1.5 text-white font-medium" style={{ backgroundColor: WT_COLOR }} data-testid={`grid-job-wt-${e.job.id}`}>Walk-through · Free</span>
                        ) : e.job.package?.name || "Clean"}
                        {!wt && e.job.price != null && <span className="font-semibold text-foreground" data-testid={`grid-job-price-${e.job.id}`}> · ${Number(e.job.price).toFixed(2)}</span>}
                      </div>
                      <div
                        className="text-[11px] truncate leading-tight flex items-center gap-1 mt-0.5"
                        style={{ color: techNames ? color : undefined }}
                        data-testid={`grid-job-tech-${e.job.id}`}
                      >
                        <User className="w-3 h-3 shrink-0" />
                        <span className={techNames ? "" : "text-muted-foreground italic"}>
                          {techNames || "Unassigned"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
